import type { ExportConfig, GeneratedFile, LanguageGenerator } from './types';
import { generateEnv, toPascal, toCamel } from './utils';

function ktType(t: string): string {
  return t === 'number' ? 'Double' : t === 'boolean' ? 'Boolean' : t === 'array' ? 'List<Any>' : t === 'object' ? 'Map<String, Any>' : 'String';
}

function genConfig(config: ExportConfig): string {
  const appEntries = config.apps
    .map((a) => `    "${a.key}" to AppConfig("${a.fromEmail}", "${a.envVarName}")`)
    .join(",\n");

  const templateEntries = config.apps
    .flatMap((a) =>
      a.templates.map((t) => `    "${t.camelName}" to TemplateConfig("${a.key}", "${t.slug}")`)
    )
    .join(",\n");

  return `// MailConfig.kt — generated by Mail Service SDK Export

data class AppConfig(val fromEmail: String, val keyEnv: String)
data class TemplateConfig(val app: String, val slug: String)

object MailConfig {
    val apps = mapOf(
${appEntries}
    )

    val templates = mapOf(
${templateEntries}
    )
}
`;
}

function genService(config: ExportConfig): string {
  const typedMethods = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const fnName = `send${toPascal(t.camelName)}`;
        if (t.schema && t.schema.length > 0) {
          const params = t.schema
            .map((f) => `${toCamel(f.key)}: ${ktType(f.type)}${f.required ? '' : '? = null'}`)
            .join(', ');
          const mapEntries = t.schema
            .map((f) => `"${f.key}" to ${toCamel(f.key)}`)
            .join(', ');
          return `    suspend fun ${fnName}(recipient: String, ${params}): Map<String, Any> =\n        send("${t.camelName}", recipient, mapOf(${mapEntries}))`;
        }
        return `    suspend fun ${fnName}(recipient: String, data: Map<String, Any> = emptyMap()): Map<String, Any> =\n        send("${t.camelName}", recipient, data)`;
      }),
    )
    .join("\n\n");

  return `// MailService.kt — generated by Mail Service SDK Export
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*

class MailService {
    private val serviceUrl = System.getenv("${config.serviceUrlVarName}")?.trimEnd('/') ?: ""
    private val client = HttpClient { install(ContentNegotiation) { json() } }

    private fun getKey(app: String) = System.getenv(MailConfig.apps[app]!!.keyEnv) ?: ""
    private fun isReady(app: String) = serviceUrl.isNotEmpty() && getKey(app).isNotEmpty()

    private suspend fun post(app: String, path: String, body: Map<String, Any?>): Map<String, Any> {
        if (!isReady(app)) return mapOf("success" to false, "error" to "not configured")
        return client.post("\$serviceUrl\$path") {
            contentType(ContentType.Application.Json)
            header("X-API-KEY", getKey(app))
            setBody(body)
        }.body()
    }

    suspend fun send(template: String, recipient: String, data: Map<String, Any?> = emptyMap()): Map<String, Any> {
        val t = MailConfig.templates[template]!!
        return post(t.app, "/v1/send", mapOf("template_slug" to t.slug, "recipient" to recipient, "data" to data))
    }

${typedMethods}
}
`;
}

export const kotlinGenerator: LanguageGenerator = {
  id: 'kotlin',
  name: 'Kotlin',
  category: 'full-sdk',
  color: '#7F52FF',
  generate(config: ExportConfig): GeneratedFile[] {
    return [
      { filename: 'MailConfig.kt', content: genConfig(config), syntax: 'kotlin' },
      { filename: 'MailService.kt', content: genService(config), syntax: 'kotlin' },
      { filename: '.env', content: generateEnv(config), syntax: 'shell' },
    ];
  },
};
